import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Shield, ArrowLeft } from "lucide-react";
import { getRoleDetails } from "../api/permissionApi";

import ModuleSelector from "../components/ModuleSelector";
import PermissionSelector from "../components/PermissionSelector";

import {
  Card,
  CardContent,
  Badge,
  Skeleton,
} from "../components/ui";

const RoleDetails = () => {
  const { id } = useParams();

  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadRole = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRoleDetails(id);
      setRole(data);
    } catch (error) {
      console.error("Error fetching role details:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadRole();
  }, [loadRole]);

  if (loading) {
    return (
      <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 antialiased">
        <Skeleton height={80} width="100%" />
        <Skeleton height={200} width="100%" />
        <Skeleton height={200} width="100%" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto antialiased">
        <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900 p-8 text-center">
          <CardContent className="p-0 text-neutral-500 dark:text-neutral-400 text-xs">
            Role details not found or failed to load.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5 antialiased">
      {/* Header Banner */}
      <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
        <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border border-neutral-200/80">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
                  {role.roleName}
                </h1>
                <Badge variant="secondary" className="text-[10px]">
                  {role.category || "General"}
                </Badge>
              </div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
                Configure module access and permission scopes for this role.
              </p>
            </div>
          </div>

          <Link
            to="/roles"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200/70 px-3 py-2 rounded-md transition-colors w-fit"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Roles
          </Link>
        </CardContent>
      </Card>

      {/* Module Selector Settings Panel */}
      <ModuleSelector
        roleId={id}
        selectedModules={role.modules}
        refreshRole={loadRole}
      />

      {/* Permission Selector Settings Panel */}
      <PermissionSelector
        roleId={id}
        selectedPermissions={role.permissions}
        refreshRole={loadRole}
      />
    </div>
  );
};

export default RoleDetails;